import type { PartialOnly } from '@ipfabric/utils/types/PartialOnly';
import { z } from 'zod';
import {
  type SiteSeparationAttributeTransformation,
  siteSeparationAttributeTransformationSchemaZ,
} from './siteSeparationAttributeTransformation';
import {
  type SiteSeparationRuleCommon,
  siteSeparationRuleCommonSchemaZ,
} from './siteSeparationRuleCommon';

export const regexSiteSeparationTypes = [
  'regexHostname',
  'regexSnmpLocation',
  'regexCloudResourceId',
] as const;

export type RegexSiteSeparationType = (typeof regexSiteSeparationTypes)[number];

export interface RegexSiteSeparationRuleBase {
  /**
   * When `regex` contains capturing groups the value of the first one can be used as a site name if `siteName` is `null`.
   * Capturing groups are numbered by counting their opening parentheses from left to right.
   */
  regex: string;
  /**
   * `null` value implies there MUST be at least one capturing group in regex
   * `string` value: non-empty site name, it MIGHT use ${...} references to regex capturing groups
   */
  siteName: string | null;
  transformation: SiteSeparationAttributeTransformation;
  type: RegexSiteSeparationType;
}

export type RegexSiteSeparationRule = SiteSeparationRuleCommon &
  RegexSiteSeparationRuleBase;

export type RegexSiteSeparationRequestRule = PartialOnly<
  RegexSiteSeparationRule,
  'id'
>;

export const regexSiteSeparationRuleBaseSchemaZ = z
  .object({
    regex: z.string().nonempty(),
    siteName: z.string().nonempty().nullable(),
    transformation: siteSeparationAttributeTransformationSchemaZ,
    type: z.enum(regexSiteSeparationTypes),
  })
  .strict();

export const regexSiteSeparationRuleSchemaZ = z
  .object({
    ...siteSeparationRuleCommonSchemaZ.shape,
    ...regexSiteSeparationRuleBaseSchemaZ.shape,
  })
  .strict();

export const regexSiteSeparationRequestRuleSchemaZ = z
  .object({
    ...regexSiteSeparationRuleSchemaZ.shape,
    id: z.string().uuid().optional(),
  })
  .strict();
